//#region src/media/primitives/select-tracks.ts
/**
* Test whether a track matches a partial-track description: every present, defined field of `filter` equals the
* track's. Absent or `undefined` filter fields don't constrain. Used to narrow candidates by a user selection (`{ id
* }`, `{ language }`, `{ height }`, …).
*
* @param track - The track to test
* @param filter - Partial-track description; only present, defined fields constrain
* @returns `true` when the track matches every constraining field
*/
function matchesPartialTrack(track, filter) {
	for (const key in filter) {
		const filterValue = filter[key];
		if (filterValue !== void 0 && track[key] !== filterValue) return false;
	}
	return true;
}
/** Missing dimensions are treated as area `0`, so a track without them ranks last. */
function pixelArea(track) {
	return (track.width ?? 0) * (track.height ?? 0);
}
/**
* Narrow to the tracks at or below `maxPixelArea`, and nothing else: no ordering, no fallback. Survivors keep their
* incoming order, and an empty result is a real answer — "none of these fit".
*
* Deliberately only the filter, because as a selection rule this composes under `applyRules`, which already owns both
* halves a caller might expect here: an empty result is skipped, so a preference can never narrow the candidate set to
* nothing; and ordering the survivors is a separate rule's job ({@link byDescendingResolution}, bandwidth ABR). Doing
* either here would duplicate the composer and give one rule two responsibilities.
*/
function tracksUnderPixelArea(tracks, maxPixelArea = Number.POSITIVE_INFINITY) {
	return tracks.filter((track) => pixelArea(track) <= maxPixelArea);
}
/**
* The smallest track area that still covers `minPixelArea`, or `undefined` when no track reaches it.
*
* The cap a surface-size rule wants when it should round _up_ to the ladder: a surface between two tiers is covered by
* the upper one, and capping at the surface's own area instead would serve a picture smaller than the surface and
* upscale it.
*
* Tracks declaring no dimensions compare as area `0` and so never cover anything, which keeps them out of the cap
* rather than pinning it to zero.
*/
function smallestCoveringPixelArea(tracks, minPixelArea) {
	const covering = tracks.map(pixelArea).filter((area) => area >= minPixelArea);
	return covering.length ? Math.min(...covering) : void 0;
}
/**
* Compare two tracks by resolution, largest first, with bandwidth as the tiebreak for renditions of identical
* dimensions. Missing dimensions are treated as area `0`, so a track without them sorts last.
*
* A comparator rather than a "highest track" function: the selection-rule chain takes the head of the list it produces,
* so ranking never has to collapse to a single track. `preferHighestResolution` is `sort` over this and nothing more.
*/
function byDescendingResolution(a, b) {
	return pixelArea(b) - pixelArea(a) || (b.bandwidth ?? 0) - (a.bandwidth ?? 0);
}
/**
* Default audio policy over a candidate list: the three-tier pick a selection-rule chain applies once it has narrowed
* the candidates.
*
* Priority: `preferredAudioLanguage` match → `DEFAULT=YES` → first track.
*/
function pickAudioTrackFromTracks(tracks, config) {
	if (config?.preferredAudioLanguage) {
		const languageMatch = tracks.find((track) => track.language === config.preferredAudioLanguage);
		if (languageMatch) return languageMatch.id;
	}
	const defaultTrack = tracks.find((track) => track.default === true);
	if (defaultTrack) return defaultTrack.id;
	return tracks[0]?.id;
}
/**
* Default text-track policy over a candidate list: the opt-in three-tier pick `switchTextTrack`'s terminal applies once
* it has narrowed the renditions to the constrained, CDN-scoped set.
*
* Priority: `preferredSubtitleLanguage` match → `DEFAULT=YES + AUTOSELECT=YES` (only when `enableDefaultTrack`) →
* `undefined` (opt-in). FORCED tracks are excluded unless `includeForcedTracks` (Apple-spec: a regular track must carry
* forced content when both exist, so a forced-only track is redundant).
*/
function pickTextTrackFromTracks(tracks, config) {
	const availableTracks = config?.includeForcedTracks ? tracks : tracks.filter((track) => !track.forced);
	if (availableTracks.length === 0) return void 0;
	const { preferredSubtitleLanguage, enableDefaultTrack = false } = config ?? {};
	if (preferredSubtitleLanguage) {
		const languageMatch = availableTracks.find((track) => track.language === preferredSubtitleLanguage);
		if (languageMatch) return languageMatch.id;
	}
	if (enableDefaultTrack) {
		const defaultTrack = availableTracks.find((track) => track.default === true);
		if (defaultTrack) return defaultTrack.id;
	}
}
//#endregion
export { byDescendingResolution, matchesPartialTrack, pickAudioTrackFromTracks, pickTextTrackFromTracks, smallestCoveringPixelArea, tracksUnderPixelArea };

//# sourceMappingURL=select-tracks.js.map